<template>
    <input
        ref="input"
        v-model="computedValue"
        :type="type === 'number' ? 'text' : type"
        :inputmode="type === 'number' ? 'decimal' : undefined"
        class="py-1 px-2 text-center outline-none rounded"
        :class="[backgroundColor]"
        @focus="onFocus"
        @blur="onBlur"
    />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { i18n } from '@/plugins/internationalization/i18n';
import NumberParser from '@/plugins/internationalization/NumberParser';

export default defineComponent({
    props: {
        value: {
            type: [String, Number],
            default: '',
        },
        type: {
            type: String,
            default: 'text',
        },
        backgroundColor: {
            type: String,
            default: 'bg-white',
        },
        // Opt-in: when true (and type="number") show the locale thousand-separated value while not
        // editing and the raw value while editing. Default false leaves all existing behaviour intact.
        grouped: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value'],
    data() {
        return { focused: false, draft: '' };
    },
    computed: {
        computedValue: {
            get(): string | number {
                // Grouped numeric fields: raw draft while editing, locale-formatted otherwise.
                if (this.$props.grouped && this.$props.type === 'number') {
                    if (this.focused) return this.draft;
                    const v = this.$props.value;
                    if (v !== '' && v !== null && v !== undefined && isFinite(Number(v))) {
                        return i18n.global.n(Number(v), 'decimal');
                    }
                    return '';
                }
                // --- original behaviour, unchanged ---
                return this.$props.value;
            },
            set(newValue: string) {
                // Grouped numeric fields: keep the raw text (caret-stable), emit the parsed number.
                if (this.$props.grouped && this.$props.type === 'number') {
                    this.draft = newValue;
                    if (newValue.trim() === '') {
                        this.$emit('update:value', '');
                        return;
                    }
                    const parsed = new NumberParser(i18n.global.locale).parse(newValue);
                    this.$emit('update:value', isNaN(parsed) ? newValue : parsed);
                    return;
                }
                // --- original behaviour, unchanged ---
                if (this.$props.type === 'number') {
                    const normalized = newValue.replace(',', '.');
                    if (['', '-', '.'].includes(normalized)) {
                        this.$emit('update:value', normalized);
                    } else {
                        this.$emit('update:value', Number(normalized));
                    }
                } else {
                    this.$emit('update:value', newValue);
                }
            },
        },
    },
    methods: {
        onFocus() {
            this.focused = true;
            if (this.$props.grouped && this.$props.type === 'number') {
                const v = this.$props.value;
                this.draft = v === 0 || v === '0' || v === '' || v === null || v === undefined ? '' : String(v);
            } else if (this.computedValue === 0 || this.computedValue === '0') {
                // --- original zero-clear, unchanged ---
                this.computedValue = '';
            }
        },
        onBlur() {
            this.focused = false;
            this.draft = '';
        },
    },
});
</script>
